import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Card, Button, Modal } from 'antd';
import { createChart, IChartApi, ISeriesApi } from 'lightweight-charts';
import { useAppTheme } from '../../theme/ThemeProvider';
import { useTranslation } from 'react-i18next';
import { CandlestickData } from 'lightweight-charts';
import { logger } from '../../utils/logger';

interface MiniChartProps {
  data: any[];
  onClose: () => void;
  title?: string;
  highlightRange?: { from: number | string; to: number | string }; // 支持秒number或字符串
  width?: number;
  height?: number;
  enableZoom?: boolean;
  largeWidth?: number;
  largeHeight?: number;
}

const MiniChart: React.FC<MiniChartProps> = ({ data, onClose, title, highlightRange, width = 520, height = 300, enableZoom = true, largeWidth = 920, largeHeight = 520 }) => {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<any>(null);
  const highlightSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const isInitialized = useRef<boolean>(false);
  const userHasInteracted = useRef<boolean>(false);
  const lastDataLength = useRef<number>(0);
  const lastDataFingerprint = useRef<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);

  // Modal chart refs
  const modalChartRef = useRef<HTMLDivElement>(null);
  const modalChartInstance = useRef<IChartApi | null>(null);
  const modalCandleSeriesRef = useRef<any>(null);
  const modalHighlightSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);

  // 兼容字符串和number，统一转为UNIX秒number
  function parseToUnixSec(val: number | string | undefined): number | undefined {
    if (typeof val === 'number') {
      // 如果输入是毫秒时间戳，转换为秒
      if (val > 1000000000000) { // 毫秒时间戳通常大于这个值
        return Math.floor(val / 1000);
      }
      return val;
    }
    if (typeof val === 'string') return Math.floor(new Date(val).getTime() / 1000);
    return undefined;
  }

  // 创建数据指纹来检测内容变化
  const getDataFingerprint = useCallback((data: any[]) => {
    if (!data.length) return '';
    // 使用第一个和最后一个数据点以及长度创建指纹
    const first = data[0];
    const last = data[data.length - 1];
    const firstTime = first.time || first.datetime || '';
    const lastTime = last.time || last.datetime || '';
    return `${firstTime}-${lastTime}-${data.length}`;
  }, []);

  // 数据处理函数
  const processData = useCallback(() => {
    if (!data.length) return [];
    
    const sortedData = [...data].sort((a, b) => {
      // 支持多种时间格式：time (Unix秒), datetime (字符串)
      const getTime = (item: any) => {
        if (item.time) return typeof item.time === 'number' ? item.time : Math.floor(new Date(item.time).getTime() / 1000);
        if (item.datetime) return Math.floor(new Date(item.datetime).getTime() / 1000);
        return 0;
      };
      return getTime(a) - getTime(b);
    });

    const chartData = sortedData.map((d: any) => {
      let timeValue: number;
      
      if (d.time) {
        // 如果有time字段，直接使用（假设是Unix秒）
        timeValue = typeof d.time === 'number' ? d.time : Math.floor(new Date(d.time).getTime() / 1000);
      } else if (d.datetime) {
        // 否则使用datetime字段转换
        timeValue = Math.floor(new Date(d.datetime).getTime() / 1000);
      } else {
        logger.warn('No valid time field found in data item:', d);
        timeValue = Math.floor(Date.now() / 1000);
      }
      
      return {
        time: timeValue as any,
        open: +d.open,
        high: +d.high,
        low: +d.low,
        close: +d.close,
        raw: d.time || d.datetime,
      };
    });

    // 检查有无重复 time
    const timeSet = new Set();
    for (const d of chartData) {
      if (timeSet.has(d.time)) {
        logger.warn('Duplicate time found in MiniChart:', d.time, d.raw);
      }
      timeSet.add(d.time);
    }

    const uniqueData: CandlestickData[] = [];
    const seen = new Set();
    for (const d of chartData) {
      if (!seen.has(d.time)) {
        uniqueData.push({
          time: d.time as any,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
        });
        seen.add(d.time);
      }
    }
    
    return uniqueData;
  }, [data]);

  // 初始化图表（只运行一次）
  useEffect(() => {
    if (!chartRef.current || isInitialized.current) return;

    const chart = createChart(chartRef.current, {
      width: width,
      height: height,
      layout: { background: { color: theme.chartBackground }, textColor: theme.chartText },
      grid: { vertLines: { color: theme.chartGrid }, horzLines: { color: theme.chartGrid } },
      timeScale: { timeVisible: true, secondsVisible: false },
      rightPriceScale: { visible: true },
    });
    chartInstance.current = chart;
    candleSeriesRef.current = chart.addCandlestickSeries();
    
    // 监听用户交互
    chart.timeScale().subscribeVisibleTimeRangeChange(() => {
      // 标记用户已经与图表交互过
      userHasInteracted.current = true;
    });
    
    isInitialized.current = true;

    // 清理函数
    return () => {
      if (chartInstance.current) {
        // 尝试移除高亮series，避免在卸载阶段重复remove导致错误
        try {
          if (highlightSeriesRef.current) {
            chartInstance.current.removeSeries(highlightSeriesRef.current as any);
            highlightSeriesRef.current = null;
          }
        } catch {}
        chartInstance.current.remove();
        chartInstance.current = null;
        candleSeriesRef.current = null;
        highlightSeriesRef.current = null;
        isInitialized.current = false;
        userHasInteracted.current = false;
        lastDataLength.current = 0;
        lastDataFingerprint.current = '';
      }
    };
  }, [theme, width, height]); // 主题/尺寸变化重建

  // 更新数据（分离逻辑）
  useEffect(() => {
    if (!candleSeriesRef.current || !data.length) return;
    
    const uniqueData = processData();
    candleSeriesRef.current.setData(uniqueData);
    
    // 创建当前数据指纹
    const currentFingerprint = getDataFingerprint(data);
    
    // 检查是否需要重置视图：
    // 1. 首次显示图表时
    // 2. 数据内容发生了变化（指纹不同）
    const dataChanged = lastDataFingerprint.current !== currentFingerprint;
    const shouldFitContent = !userHasInteracted.current || dataChanged;
    
    if (shouldFitContent && chartInstance.current) {
      chartInstance.current.timeScale().fitContent();
      // 如果是因为数据变化而重置，则重置用户交互标志
      if (dataChanged) {
        userHasInteracted.current = false;
      }
    }
    
    lastDataLength.current = data.length;
    lastDataFingerprint.current = currentFingerprint;
  }, [data, processData, getDataFingerprint]);

  // 处理高亮区间（独立的effect）
  useEffect(() => {
    if (!chartInstance.current) return;

    // 先移除旧的高亮series，避免在主题切换/重建时重复remove
    try {
      if (highlightSeriesRef.current) {
        chartInstance.current.removeSeries(highlightSeriesRef.current as any);
        highlightSeriesRef.current = null;
      }
    } catch {}

    if (!highlightRange) return;

    const fromTime = parseToUnixSec(highlightRange.from);
    const toTime = parseToUnixSec(highlightRange.to);
    if (fromTime === undefined || toTime === undefined) return;

    const uniqueData = processData();
    const highlightData = uniqueData.map(d => ({
      time: d.time,
      value: d.low,
      color: (Number(d.time) >= fromTime && Number(d.time) <= toTime)
        ? 'rgba(255, 215, 0, 0.3)' : 'rgba(0,0,0,0)'
    }));

    const series = chartInstance.current.addHistogramSeries({
      color: 'rgba(255, 215, 0, 0.3)',
      priceScaleId: '',
      base: 0,
    });
    series.setData(highlightData);
    highlightSeriesRef.current = series as any;
  }, [highlightRange, processData, theme]);

  // ---------- Modal chart lifecycle ----------
  useEffect(() => {
    if (!showModal) return;
    if (!modalChartRef.current) return;

    // create modal chart
    const chart = createChart(modalChartRef.current, {
      width: largeWidth,
      height: largeHeight,
      layout: { background: { color: theme.chartBackground }, textColor: theme.chartText },
      grid: { vertLines: { color: theme.chartGrid }, horzLines: { color: theme.chartGrid } },
      timeScale: { timeVisible: true, secondsVisible: false },
      rightPriceScale: { visible: true },
    });
    modalChartInstance.current = chart;
    modalCandleSeriesRef.current = chart.addCandlestickSeries();

    // set data
    const uniqueData = processData();
    modalCandleSeriesRef.current.setData(uniqueData);
    chart.timeScale().fitContent();

    // highlight range if provided
    if (highlightRange) {
      const fromTime = parseToUnixSec(highlightRange.from);
      const toTime = parseToUnixSec(highlightRange.to);
      if (fromTime !== undefined && toTime !== undefined) {
        const highlightData = uniqueData.map(d => ({
          time: d.time,
          value: d.low,
          color: (Number(d.time) >= fromTime && Number(d.time) <= toTime)
            ? 'rgba(255, 215, 0, 0.3)' : 'rgba(0,0,0,0)'
        }));
        const series = chart.addHistogramSeries({ color: 'rgba(255, 215, 0, 0.3)', priceScaleId: '', base: 0 });
        series.setData(highlightData);
        modalHighlightSeriesRef.current = series as any;
      }
    }

    return () => {
      try {
        if (modalHighlightSeriesRef.current && modalChartInstance.current) {
          modalChartInstance.current.removeSeries(modalHighlightSeriesRef.current as any);
          modalHighlightSeriesRef.current = null;
        }
      } catch {}
      if (modalChartInstance.current) {
        modalChartInstance.current.remove();
        modalChartInstance.current = null;
      }
      modalCandleSeriesRef.current = null;
    };
  }, [showModal, theme, largeWidth, largeHeight, processData, highlightRange]);

  return (
    <Card
      title={title || t('analysis.klineSimilarityAnalysis')}
      size="small"
      style={{ 
        width: width, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)', 
        marginBottom: 16,
        borderRadius: 8,
        background: theme.surfaceBackground,
        color: theme.textPrimary,
        borderColor: theme.surfaceBorder
      }}
      extra={
        <div style={{ display: 'flex', gap: 8 }}>
          {enableZoom && (
            <Button size="small" onClick={() => setShowModal(true)} style={{ borderRadius: 6 }}>
              {t('common.enlarge')}
            </Button>
          )}
          <Button 
            size="small" 
            onClick={onClose}
            style={{ borderRadius: 6 }}
          >
            {t('common.close')}
          </Button>
        </div>
      }
      styles={{ 
        body: { padding: 0 },
        header: { 
          background: `linear-gradient(135deg, ${theme.accentGradientFrom} 0%, ${theme.accentGradientTo} 100%)`,
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }
      }}
    >
      <div ref={chartRef} style={{ width: width, height: height }} />
      <Modal
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        width={largeWidth + 48}
        bodyStyle={{ padding: 0 }}
        title={title || t('analysis.klineSimilarityAnalysis')}
      >
        <div ref={modalChartRef} style={{ width: largeWidth, height: largeHeight }} />
      </Modal>
    </Card>
  );
};

export default MiniChart;